/**
 * Megamenu Navigation Item Controller
 *
 * Handles megamenu navigation item interactions, submenu positioning,
 * keyboard navigation, and accessibility features.
 */
class MegamenuNavItem {

	/**
	 * Singleton instance.
	 * @type {MegamenuNavItem|null}
	 */
	static instance = null;

	/**
	 * Maximum columns for positioning calculations.
	 * @type {number}
	 */
	maxColumns = 3;

	/**
	 * Debounce delay for resize handler (ms).
	 * @type {number}
	 */
	resizeDebounceDelay = 250;

	/**
	 * Mobile breakpoint (px).
	 * @type {number}
	 */
	mobileBreakpoint = 768;

	/**
	 * Bound event handler references for cleanup.
	 */
	handlers = {
		documentClick: null,
		documentKeydown: null,
		windowResize: null,
	};

	/**
	 * Label event handlers map for cleanup.
	 * @type {Map}
	 */
	labelHandlers = new Map();

	/**
	 * Resize debounce timer.
	 * @type {number|null}
	 */
	resizeTimer = null;

	/**
	 * All nav items.
	 * @type {NodeList}
	 */
	navItems = null;

	/**
	 * Initializes megamenu navigation items.
	 */
	constructor() {

		// Singleton pattern
		if ( MegamenuNavItem.instance ) {

			return MegamenuNavItem.instance;

		}

		this.navItems = document.querySelectorAll(
			'.b-megamenu-nav-item.has-submenu',
		);

		if ( this.navItems.length === 0 ) {

			return;

		}

		this.init();

		MegamenuNavItem.instance = this;

	}

	/**
	 * Initializes event listeners for nav items.
	 */
	init = () => {

		this.navItems.forEach( ( item ) => {

			// Guard: skip if already initialized
			if ( item.dataset.navItemInitialized === 'true' ) {

				return;

			}

			item.dataset.navItemInitialized = 'true';

			const label = item.querySelector( '.b-megamenu-nav-item__label' );

			if ( !label ) {

				return;

			}

			label.setAttribute( 'role', 'button' );
			label.setAttribute( 'aria-haspopup', 'true' );
			label.setAttribute( 'aria-expanded', 'false' );
			label.setAttribute( 'tabindex', '0' );

			/**
			 * Click handler for nav item label.
			 *
			 * @param {Event} e - Click event.
			 */
			const clickHandler = ( e ) => {

				// Allow hash navigation if target element exists (supports full URLs with hash)
				const anchor = e.target.closest( 'a[href]' );
				if ( anchor ) {

					const href = anchor.getAttribute( 'href' );
					if ( href ) {

						// Parse URL to extract hash
						let hash;
						try {

							const url = new URL( href, window.location.href );

							if (
								url.origin !== window.location.origin ||
								url.pathname !== window.location.pathname
							) {

								return;

							}

							hash = url.hash;

						} catch ( error ) {

							// If URL parsing fails, check if href starts with #
							if ( href.startsWith( '#' ) ) {

								hash = href;

							} else {

								// Not a hash - allow default navigation
								return;

							}

						}

						if ( hash && hash !== '#' && hash.length > 1 ) {

							const targetId = hash.slice( 1 );
							const targetElement =
								document.getElementById( targetId );
							if ( targetElement ) {

								// Hash navigation - close submenu and allow default behavior
								this.closeAllSubmenus();
								return;

							}

						}

					}

				}

				e.preventDefault();
				e.stopPropagation();
				this.toggleSubmenu( item );

			};

			/**
			 * Keyboard navigation handler for nav item label.
			 *
			 * @param {Event} e - Keydown event.
			 */
			const keydownHandler = ( e ) => {

				if ( e.key === 'Enter' || e.key === ' ' ) {

					// Allow hash navigation if target element exists (supports full URLs with hash)
					const anchor = e.target.closest( 'a[href]' );
					if ( anchor ) {

						const href = anchor.getAttribute( 'href' );
						if ( href ) {

							// Parse URL to extract hash
							let hash;
							try {

								const url = new URL( href, window.location.href );

								// Check if same page (same origin + pathname)
								if (
									url.origin !== window.location.origin ||
									url.pathname !== window.location.pathname
								) {

									// Different page - allow default navigation
									return;

								}

								hash = url.hash;

							} catch ( error ) {

								// If URL parsing fails, check if href starts with #
								if ( href.startsWith( '#' ) ) {

									hash = href;

								} else {

									return;

								}

							}

							if ( hash && hash !== '#' && hash.length > 1 ) {

								const targetId = hash.slice( 1 );
								const targetElement =
									document.getElementById( targetId );
								if ( targetElement ) {

									// Hash navigation - close submenu and allow default behavior
									this.closeAllSubmenus();
									return;

								}

							}

						}

					}

					e.preventDefault();
					e.stopPropagation();
					this.toggleSubmenu( item );

				}

			};

			label.addEventListener( 'click', clickHandler );
			label.addEventListener( 'keydown', keydownHandler );

			this.labelHandlers.set( label, {
				click: clickHandler,
				keydown: keydownHandler,
			} );

		} );

		this.setupGlobalListeners();

	};

	/**
	 * Setup global event listeners (document click, escape, resize).
	 */
	setupGlobalListeners = () => {

		this.handlers.documentClick = ( e ) => {

			const columnsWrapper = e.target.closest(
				'.b-megamenu-item__columns-wrapper',
			);

			if ( !columnsWrapper ) {

				this.closeAllSubmenus();

			}

		};

		this.handlers.documentKeydown = ( e ) => {

			if ( e.key === 'Escape' ) {

				this.closeAllSubmenus();

			}

		};

		this.handlers.windowResize = () => {

			if ( this.resizeTimer ) {

				clearTimeout( this.resizeTimer );

			}

			this.resizeTimer = setTimeout( () => {

				const activeItems = document.querySelectorAll(
					'.b-megamenu-nav-item.has-submenu.is-active',
				);

				activeItems.forEach( ( item ) => {

					this.positionSubmenu( item );
					this.updateSubmenuImagePosition( item );
					this.updateColumnsWrapperHeight( item );

				} );

				this.resizeTimer = null;

			}, this.resizeDebounceDelay );

		};

		document.addEventListener( 'click', this.handlers.documentClick );
		document.addEventListener( 'keydown', this.handlers.documentKeydown );
		window.addEventListener( 'resize', this.handlers.windowResize );

	};

	/**
	 * Cleanup event listeners.
	 */
	destroy = () => {

		if ( this.handlers.documentClick ) {

			document.removeEventListener( 'click', this.handlers.documentClick );

		}

		if ( this.handlers.documentKeydown ) {

			document.removeEventListener(
				'keydown',
				this.handlers.documentKeydown,
			);

		}

		if ( this.handlers.windowResize ) {

			window.removeEventListener( 'resize', this.handlers.windowResize );

		}

		this.labelHandlers.forEach( ( handler, label ) => {

			label.removeEventListener( 'click', handler.click );
			label.removeEventListener( 'keydown', handler.keydown );

		} );

		this.labelHandlers.clear();

		if ( this.resizeTimer ) {

			clearTimeout( this.resizeTimer );
			this.resizeTimer = null;

		}

		MegamenuNavItem.instance = null;

	};

	/**
	 * Sanitizes image URL to prevent XSS attacks.
	 *
	 * @param {string} url - URL to sanitize.
	 * @return {string} Sanitized URL or empty string if invalid.
	 */
	sanitizeImageUrl = ( url ) => {

		if ( !url || typeof url !== 'string' ) {

			return '';

		}

		// Block javascript: and data: protocols
		const lowerUrl = url.toLowerCase().trim();
		if (
			lowerUrl.startsWith( 'javascript:' ) ||
			lowerUrl.startsWith( 'data:' )
		) {

			return '';

		}

		return url;

	};

	/**
	 * Sanitizes alt text to prevent XSS.
	 *
	 * @param {string} alt - Alt text to sanitize.
	 * @return {string} Sanitized alt text.
	 */
	sanitizeAltText = ( alt ) => {

		if ( !alt || typeof alt !== 'string' ) {

			return '';

		}

		const div = document.createElement( 'div' );
		div.textContent = alt;
		return div.innerHTML;

	};

	/**
	 * Calculates column metrics (DRY - used in 3 methods).
	 *
	 * @param {HTMLElement} columnsWrapper - Columns wrapper element.
	 * @return {Object|null} Column metrics or null if invalid.
	 */
	calculateColumnMetrics = ( columnsWrapper ) => {

		if ( !columnsWrapper ) {

			return null;

		}

		try {

			const wrapperStyle = window.getComputedStyle( columnsWrapper );
			const {
				gap: cssGap,
				columnGap,
				paddingLeft: cssPaddingLeft,
				paddingRight: cssPaddingRight,
			} = wrapperStyle;

			const gap = parseFloat( cssGap || columnGap || '0px' ) || 0;
			const paddingLeft = parseFloat( cssPaddingLeft ) || 0;
			const paddingRight = parseFloat( cssPaddingRight ) || 0;

			const wrapperWidth = columnsWrapper.offsetWidth;
			const availableWidth = wrapperWidth - paddingLeft - paddingRight;
			const totalGaps = gap * ( this.maxColumns - 1 );
			const columnWidth = ( availableWidth - totalGaps ) / this.maxColumns;

			return {
				gap,
				paddingLeft,
				paddingRight,
				wrapperWidth,
				availableWidth,
				columnWidth,
			};

		} catch ( error ) {

			return null;

		}

	};

	/**
	 * Updates ARIA attributes for nav item.
	 *
	 * @param {HTMLElement} item - Nav item element.
	 * @param {boolean} isExpanded - Whether submenu is expanded.
	 */
	updateAriaAttributes = ( item, isExpanded ) => {

		const label = item.querySelector( '.b-megamenu-nav-item__label' );

		if ( !label ) {

			return;

		}

		label.setAttribute( 'aria-expanded', isExpanded ? 'true' : 'false' );

	};

	/**
	 * Opens submenu without toggling.
	 *
	 * @param {HTMLElement} item - Nav item element.
	 */
	openSubmenu = ( item ) => {

		const isActive = item.classList.contains( 'is-active' );

		if ( isActive ) {

			return;

		}

		item.classList.add( 'is-active' );
		this.updateAriaAttributes( item, true );
		this.positionSubmenu( item );
		this.addSubmenuImage( item );

		const columnsWrapper = item.closest(
			'.b-megamenu-item__columns-wrapper',
		);

		if ( columnsWrapper ) {

			this.updateColumnVisibility( columnsWrapper );

		}

		this.updateColumnsWrapperHeight( item );

	};

	/**
	 * Toggles submenu visibility, closing siblings at same depth.
	 *
	 * @param {HTMLElement} item - Nav item element.
	 */
	toggleSubmenu = ( item ) => {

		const isActive = item.classList.contains( 'is-active' );

		this.closeSiblingsAndChildren( item );

		if ( !isActive ) {

			item.classList.add( 'is-active' );
			this.updateAriaAttributes( item, true );
			this.positionSubmenu( item );
			this.addSubmenuImage( item );

			const columnsWrapper = item.closest(
				'.b-megamenu-item__columns-wrapper',
			);

			if ( columnsWrapper ) {

				this.updateColumnVisibility( columnsWrapper );

				// Scroll to top on desktop if dropdown has overflow scrolling
				const isMobile = window.matchMedia(
					`(max-width: ${this.mobileBreakpoint}px)`,
				).matches;

				if ( !isMobile ) {

					// Check if columns wrapper has scrolling (overflow is present)
					const hasScroll = columnsWrapper.scrollHeight > columnsWrapper.clientHeight;

					if ( hasScroll ) {

						// Smooth scroll to top
						columnsWrapper.scrollTo( {
							top: 0,
							behavior: 'smooth',
						} );

					}

				}

			}

			this.updateColumnsWrapperHeight( item );
			this.notifyContentChanged( item );

		} else {

			item.classList.remove( 'is-active' );
			this.updateAriaAttributes( item, false );
			this.removeSubmenuImage( item );

			const nestedActive = item.querySelectorAll(
				'.b-megamenu-nav-item.has-submenu.is-active',
			);

			nestedActive.forEach( ( nested ) => {

				nested.classList.remove( 'is-active' );
				this.updateAriaAttributes( nested, false );
				this.removeSubmenuImage( nested );

			} );

			const columnsWrapper = item.closest(
				'.b-megamenu-item__columns-wrapper',
			);

			if ( columnsWrapper ) {

				this.updateColumnVisibility( columnsWrapper );

			}

			this.updateColumnsWrapperHeight( item );

			// Notify dropdown to update scroll
			this.notifyContentChanged( item );

		}

	};

	/**
	 * Notify megamenu dropdown that content changed (for scroll update)
	 * @param {HTMLElement} item - Nav item element
	 */
	notifyContentChanged = ( item ) => {

		const megamenu = item.closest( '.b-megamenu' );

		if ( megamenu ) {

			const event = new CustomEvent( 'megamenu:content-changed', {
				bubbles: true,
				detail: { item },
			} );

			megamenu.dispatchEvent( event );

		}

	};

	/**
	 * Closes sibling submenus and nested children, preserving parent chain.
	 *
	 * @param {HTMLElement} item - Nav item being opened.
	 */
	closeSiblingsAndChildren = ( item ) => {

		const columnsWrapper = item.closest(
			'.b-megamenu-item__columns-wrapper',
		);

		if ( !columnsWrapper ) {

			return;

		}

		const itemDepth = this.calculateDepth( item );
		const allActiveItems = columnsWrapper.querySelectorAll(
			'.b-megamenu-nav-item.has-submenu.is-active',
		);

		allActiveItems.forEach( ( activeItem ) => {

			if ( activeItem === item || activeItem.contains( item ) ) {

				return;

			}

			const activeDepth = this.calculateDepth( activeItem );

			if ( activeDepth === itemDepth ) {

				activeItem.classList.remove( 'is-active' );
				this.updateAriaAttributes( activeItem, false );
				this.removeSubmenuImage( activeItem );

				const nestedActive = activeItem.querySelectorAll(
					'.b-megamenu-nav-item.has-submenu.is-active',
				);

				nestedActive.forEach( ( nested ) => {

					nested.classList.remove( 'is-active' );
					this.updateAriaAttributes( nested, false );
					this.removeSubmenuImage( nested );

				} );

			}

		} );

	};

	/**
	 * Calculates nesting depth of nav item (0 = top level).
	 *
	 * @param {HTMLElement} item - Nav item element.
	 * @return {number} Nesting depth.
	 */
	calculateDepth = ( item ) => {

		let depth = 0;
		let parent = item.parentElement;

		while ( parent && !parent.classList.contains( 'b-megamenu-item-column' ) ) {

			if ( parent.classList.contains( 'b-megamenu-nav-item__submenu' ) ) {

				depth++;

			}

			parent = parent.parentElement;

		}

		return depth;

	};

	/**
	 * Positions submenu absolutely within columns wrapper.
	 *
	 * @param {HTMLElement} item - Nav item element.
	 */
	positionSubmenu = ( item ) => {

		const submenu = item.querySelector( '.b-megamenu-nav-item__submenu' );

		if ( !submenu ) {

			return;

		}

		const isMobile = window.matchMedia(
			`(max-width: ${this.mobileBreakpoint}px)`,
		).matches;

		if ( isMobile ) {

			submenu.style.position = 'static';
			submenu.style.width = '100%';
			submenu.style.left = 'auto';
			submenu.style.top = 'auto';
			return;

		}

		const columnsWrapper = item.closest(
			'.b-megamenu-item__columns-wrapper',
		);

		if ( !columnsWrapper ) {

			return;

		}

		const columns = Array.from(
			columnsWrapper.querySelectorAll( '.b-megamenu-item-column' ),
		);

		if ( columns.length === 0 ) {

			return;

		}

		const parentColumn = item.closest( '.b-megamenu-item-column' );

		if ( !parentColumn ) {

			return;

		}

		const metrics = this.calculateColumnMetrics( columnsWrapper );

		if ( !metrics ) {

			return;

		}

		const { gap, paddingLeft, columnWidth } = metrics;

		const parentColumnIndex = columns.indexOf( parentColumn );
		const depth = this.calculateDepth( item );
		const targetColumnIndex = parentColumnIndex + 1;

		const calculatedLeft =
			depth === 0
				? ( columnWidth + gap ) * targetColumnIndex + paddingLeft
				: columnWidth + gap;

		submenu.style.position = 'absolute';
		submenu.style.top = '0';
		submenu.style.left = `${calculatedLeft}px`;
		submenu.style.width = `${columnWidth}px`;

	};

	/**
	 * Updates wrapper height to fit tallest visible column/submenu.
	 *
	 * @param {HTMLElement} item - Nav item element.
	 */
	updateColumnsWrapperHeight = ( item ) => {

		const columnsWrapper = item.closest(
			'.b-megamenu-item__columns-wrapper',
		);

		if ( !columnsWrapper ) {

			return;

		}

		const isMobile = window.matchMedia( '(max-width: 768px)' ).matches;

		if ( isMobile ) {

			columnsWrapper.style.minHeight = 'auto';
			return;

		}

		const columns = Array.from(
			columnsWrapper.querySelectorAll( '.b-megamenu-item-column' ),
		);

		const activeSubmenus = Array.from(
			columnsWrapper.querySelectorAll( '.b-megamenu-nav-item__submenu' ),
		).filter( ( submenu ) => {

			const parent = submenu.closest( '.b-megamenu-nav-item.has-submenu' );
			return parent && parent.classList.contains( 'is-active' );

		} );

		let maxHeight = 0;

		columns.forEach( ( column ) => {

			const height = column.offsetHeight;
			if ( height > maxHeight ) {

				maxHeight = height;

			}

		} );

		activeSubmenus.forEach( ( submenu ) => {

			const height = submenu.offsetHeight;
			if ( height > maxHeight ) {

				maxHeight = height;

			}

		} );

		if ( maxHeight > 0 ) {

			// Check if maxHeight is already set by megamenu.js (dropdown scroll management)
			// If so, don't override minHeight as it would conflict
			const currentMaxHeight = columnsWrapper.style.maxHeight;

			if ( !currentMaxHeight || currentMaxHeight === '' ) {

				columnsWrapper.style.minHeight = `${maxHeight}px`;

			} else {

				// maxHeight is set (dropdown scroll active) - ensure minHeight doesn't exceed it
				const maxHeightValue = parseFloat( currentMaxHeight ) || 0;

				if ( maxHeightValue > 0 ) {

					// Set minHeight to the smaller of content height or maxHeight constraint
					columnsWrapper.style.minHeight = `${Math.min(
						maxHeight,
						maxHeightValue,
					)}px`;

				} else {

					columnsWrapper.style.minHeight = `${maxHeight}px`;

				}

			}

		}

	};

	/**
	 * Closes specific submenu.
	 *
	 * @param {HTMLElement} item - Nav item element.
	 */
	closeSubmenu = ( item ) => {

		item.classList.remove( 'is-active' );
		this.updateColumnsWrapperHeight( item );

	};

	/**
	 * Closes all open submenus and removes all submenu images.
	 */
	closeAllSubmenus = () => {

		this.navItems.forEach( ( item ) => {

			item.classList.remove( 'is-active' );
			this.updateAriaAttributes( item, false );

		} );

		const allImageColumns = document.querySelectorAll(
			'.b-megamenu-item-column.is-submenu-image',
		);
		allImageColumns.forEach( ( imageColumn ) => {

			imageColumn.remove();

		} );

		const wrappers = document.querySelectorAll(
			'.b-megamenu-item__columns-wrapper',
		);

		wrappers.forEach( ( wrapper ) => {

			this.updateColumnVisibility( wrapper );

			const firstItem = wrapper.querySelector( '.b-megamenu-nav-item' );

			if ( firstItem ) {

				this.updateColumnsWrapperHeight( firstItem );

			}

		} );

	};

	/**
	 * Adds submenu image column if nav-item has image data (desktop only, column 0).
	 *
	 * @param {HTMLElement} item - Nav item element.
	 */
	addSubmenuImage = ( item ) => {

		const megamenuItem = item.closest( '.b-megamenu-item' );

		if ( !megamenuItem || !megamenuItem.classList.contains( 'is-dropdown' ) ) {

			return;

		}

		const isMobile = window.matchMedia(
			`(max-width: ${this.mobileBreakpoint}px)`,
		).matches;

		if ( isMobile ) {

			return;

		}

		const imageUrl = item.getAttribute( 'data-submenu-image-url' );
		const sanitizedUrl = this.sanitizeImageUrl( imageUrl );

		if ( !sanitizedUrl ) {

			return;

		}

		const columnsWrapper = item.closest(
			'.b-megamenu-item__columns-wrapper',
		);

		if ( !columnsWrapper ) {

			return;

		}

		const parentColumn = item.closest( '.b-megamenu-item-column' );

		if ( !parentColumn ) {

			return;

		}

		const allColumns = Array.from(
			columnsWrapper.querySelectorAll( '.b-megamenu-item-column' ),
		);
		const columnIndex = allColumns.indexOf( parentColumn );

		if ( columnIndex !== 0 ) {

			return;

		}

		const existingImages = columnsWrapper.querySelectorAll(
			'.b-megamenu-item-column.is-submenu-image',
		);

		existingImages.forEach( ( existingImage ) => {

			existingImage.remove();

		} );

		const metrics = this.calculateColumnMetrics( columnsWrapper );

		if ( !metrics ) {

			return;

		}

		const { gap, paddingLeft, columnWidth } = metrics;

		const imageAlt = item.getAttribute( 'data-submenu-image-alt' ) || '';
		const sanitizedAlt = this.sanitizeAltText( imageAlt );

		const targetColumnIndex = 2;
		const calculatedLeft =
			( columnWidth + gap ) * targetColumnIndex + paddingLeft;

		const imageColumn = document.createElement( 'div' );
		imageColumn.className =
			'wp-block-wombat-megamenu-item-column b-megamenu-item-column is-submenu-image';

		imageColumn.style.left = `${calculatedLeft}px`;
		imageColumn.style.width = `${columnWidth}px`;

		const imageInner = document.createElement( 'div' );
		imageInner.className = 'b-megamenu-item-column__inner';

		const img = document.createElement( 'img' );
		img.src = sanitizedUrl;
		img.alt = sanitizedAlt;
		img.className = 'b-megamenu-submenu-image';
		img.loading = 'lazy';
		img.decoding = 'async';

		if ( !sanitizedAlt ) {

			img.setAttribute( 'role', 'presentation' );

		}

		imageInner.appendChild( img );
		imageColumn.appendChild( imageInner );

		columnsWrapper.appendChild( imageColumn );

		this.notifyContentChanged( item );

	};

	/**
	 * Updates submenu image position on resize.
	 *
	 * @param {HTMLElement} item - Nav item element.
	 */
	updateSubmenuImagePosition = ( item ) => {

		const columnsWrapper = item.closest(
			'.b-megamenu-item__columns-wrapper',
		);

		if ( !columnsWrapper ) {

			return;

		}

		const imageColumn = columnsWrapper.querySelector(
			'.b-megamenu-item-column.is-submenu-image',
		);

		if ( !imageColumn ) {

			return;

		}

		const metrics = this.calculateColumnMetrics( columnsWrapper );

		if ( !metrics ) {

			return;

		}

		const { gap, paddingLeft, columnWidth } = metrics;

		const targetColumnIndex = 2;
		const calculatedLeft =
			( columnWidth + gap ) * targetColumnIndex + paddingLeft;

		imageColumn.style.left = `${calculatedLeft}px`;
		imageColumn.style.width = `${columnWidth}px`;

	};

	/**
	 * Updates column/gallery visibility based on active submenus and images.
	 * Manages .is-covered class and handles submenu image add/remove.
	 *
	 * @param {HTMLElement} columnsWrapper - Columns wrapper element.
	 */
	updateColumnVisibility = ( columnsWrapper ) => {

		if ( !columnsWrapper ) {

			return;

		}

		const allChildren = Array.from( columnsWrapper.children ).filter(
			( child ) =>
				!child.classList.contains( 'b-megamenu-nav-item__submenu' ) &&
				!child.classList.contains( 'is-submenu-image' ),
		);

		if ( allChildren.length === 0 ) {

			return;

		}

		allChildren.forEach( ( child, childIndex ) => {

			const isCovered = this.isChildCovered(
				child,
				childIndex,
				allChildren,
				columnsWrapper,
			);

			if ( isCovered ) {

				child.classList.add( 'is-covered' );

			} else {

				child.classList.remove( 'is-covered' );

			}

		} );

		const submenuImage = columnsWrapper.querySelector(
			'.b-megamenu-item-column.is-submenu-image',
		);

		if ( submenuImage ) {

			const imageRect = submenuImage.getBoundingClientRect();

			const allActiveSubmenus = columnsWrapper.querySelectorAll(
				'.b-megamenu-nav-item.is-active > .b-megamenu-nav-item__submenu',
			);

			const submenuRects = Array.from( allActiveSubmenus ).map(
				( submenu ) => ( {
					submenu,
					rect: submenu.getBoundingClientRect(),
				} ),
			);

			for ( const { rect: submenuRect } of submenuRects ) {

				const overlaps = !(
					submenuRect.right < imageRect.left ||
					submenuRect.left > imageRect.right ||
					submenuRect.bottom < imageRect.top ||
					submenuRect.top > imageRect.bottom
				);

				if ( overlaps ) {

					submenuImage.remove();
					break;

				}

			}

		} else {

			const column0Items = columnsWrapper.querySelectorAll(
				'.b-megamenu-item-column .b-megamenu-nav-item.has-submenu.is-active[data-submenu-image-url]',
			);

			for ( const item of column0Items ) {

				const parentColumn = item.closest( '.b-megamenu-item-column' );

				if ( !parentColumn ) {

					continue;

				}

				const columnIndex =
					Array.from( allChildren ).indexOf( parentColumn );

				if ( columnIndex !== 0 ) {

					continue;

				}

				const depth = this.calculateDepth( item );
				if ( depth !== 0 ) {

					continue;

				}

				const allActiveSubmenus = columnsWrapper.querySelectorAll(
					'.b-megamenu-nav-item.is-active > .b-megamenu-nav-item__submenu',
				);

				let position2Covered = false;

				for ( const submenu of allActiveSubmenus ) {

					const submenuParent = submenu.closest(
						'.b-megamenu-nav-item.has-submenu',
					);
					const submenuParentColumn = submenuParent.closest(
						'.b-megamenu-item-column',
					);
					const parentPosition =
						Array.from( allChildren ).indexOf( submenuParentColumn );
					const submenuPosition = parentPosition + 1;

					if ( submenuPosition === 2 ) {

						position2Covered = true;
						break;

					}

				}

				if ( !position2Covered ) {

					this.addSubmenuImage( item );

				}

				break;

			}

		}

	};

	/**
	 * Checks if child element is covered by active submenu or submenu image.
	 *
	 * @param {HTMLElement} child - Child element to check.
	 * @param {number} childIndex - Index in allChildren array.
	 * @param {Array} allChildren - All children (columns, galleries).
	 * @param {HTMLElement} columnsWrapper - Columns wrapper element.
	 * @return {boolean} True if covered.
	 */
	isChildCovered = ( child, childIndex, allChildren, columnsWrapper ) => {

		const submenuImage = columnsWrapper.querySelector(
			'.b-megamenu-item-column.is-submenu-image',
		);

		if ( submenuImage && childIndex === 2 ) {

			return true;

		}

		const activeItems = Array.from(
			columnsWrapper.querySelectorAll(
				'.b-megamenu-nav-item.has-submenu.is-active',
			),
		).filter( ( item ) => {

			const depth = this.calculateDepth( item );
			return depth === 0;

		} );

		for ( const item of activeItems ) {

			const parentColumn = item.closest( '.b-megamenu-item-column' );

			if ( !parentColumn ) {

				continue;

			}

			const parentPosition = allChildren.indexOf( parentColumn );
			const coveredPosition = parentPosition + 1;

			if ( coveredPosition === childIndex ) {

				return true;

			}

		}

		return false;

	};

	/**
	 * Removes submenu image column.
	 *
	 * @param {HTMLElement} item - Nav item element.
	 */
	removeSubmenuImage = ( item ) => {

		const columnsWrapper = item.closest(
			'.b-megamenu-item__columns-wrapper',
		);

		if ( !columnsWrapper ) {

			return;

		}

		const imageColumn = columnsWrapper.querySelector(
			'.b-megamenu-item-column.is-submenu-image',
		);

		if ( imageColumn ) {

			imageColumn.remove();

			const baseColumns = Array.from(
				columnsWrapper.querySelectorAll( '.b-megamenu-item-column' ),
			).filter( ( col ) => !col.classList.contains( 'is-submenu-image' ) );

			if ( baseColumns[2] ) {

				baseColumns[2].classList.remove( 'is-covered' );

			}

		}

	};

}

// Initialize MegamenuNavItem (Singleton manages all nav items)
new MegamenuNavItem();
